import { useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Table, Button, Badge, Modal, Form, Alert, ButtonGroup } from 'react-bootstrap';
import { useUsers } from '@hooks';
import { useCourses } from '@hooks';

const splitNameParts = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) {
    return {
      firstName: '',
      lastName: '',
    };
  }

  const parts = normalized.split(/\s+/);
  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' '),
  };
};

/**
 * User Manager Component
 * Handles CRUD operations for users
 */
export function UserManager({ onViewProgress }) {
  const { users, loading, error, createUser, updateUser, deleteUser, setPassword } = useUsers();
  const { courses } = useCourses();
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'student',
    organizationName: '',
    managedCourses: [],
    courseLicenseLimit: '',
    isActive: true,
  });
  const [formError, setFormError] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordResetUser, setPasswordResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState(null);

  /**
   * Handle opening create modal
   */
  const handleCreate = () => {
    setEditingUser(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      role: 'student',
      organizationName: '',
      managedCourses: [],
      courseLicenseLimit: '',
      isActive: true,
    });
    setFormError(null);
    setShowModal(true);
  };

  /**
   * Handle opening edit modal
   */
  const handleEdit = (user) => {
    const fallbackNameParts = splitNameParts(user.name);

    setEditingUser(user);
    setFormData({
      firstName: String(user.firstName || fallbackNameParts.firstName || ''),
      lastName: String(user.lastName || fallbackNameParts.lastName || ''),
      email: user.email,
      role: user.role,
      organizationName: String(user.organizationName || ''),
      managedCourses: user.managedCourses || [],
      courseLicenseLimit: user.courseLicenseLimit != null ? String(user.courseLicenseLimit) : '',
      isActive: user.isActive,
    });
    setFormError(null);
    setShowModal(true);
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);

    const normalizedFirstName = String(formData.firstName || '').trim();
    const normalizedLastName = String(formData.lastName || '').trim();
    const normalizedEmail = String(formData.email || '').trim().toLowerCase();
    const normalizedOrganizationName = String(formData.organizationName || '').trim();
    const fullName = `${normalizedFirstName} ${normalizedLastName}`.trim();

    // Validation
    if (!normalizedFirstName) {
      setFormError('First name is required');
      return;
    }
    if (!normalizedLastName) {
      setFormError('Last name is required');
      return;
    }
    if (!normalizedEmail) {
      setFormError('Email is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setFormError('Invalid email format');
      return;
    }
    if (formData.role === 'course_manager' && !normalizedOrganizationName) {
      setFormError('Organization is required for course managers');
      return;
    }

    try {
      const payload = {
        ...formData,
        firstName: normalizedFirstName,
        lastName: normalizedLastName,
        name: fullName,
        email: normalizedEmail,
        organizationName: formData.role === 'course_manager' ? normalizedOrganizationName : null,
        managedCourses: formData.role === 'course_manager' ? (formData.managedCourses || []) : [],
        courseLicenseLimit: formData.role === 'course_manager' && formData.courseLicenseLimit !== ''
          ? Number(formData.courseLicenseLimit)
          : 0,
      };

      if (editingUser) {
        await updateUser(editingUser.id, payload);
      } else {
        const sendNotificationEmail = window.confirm('Do you want to send a notification email to this user?');
        await createUser(payload, { sendNotificationEmail });
      }
      setShowModal(false);
    } catch (err) {
      setFormError(err.message);
    }
  };

  /**
   * Handle user deletion
   */
  const handleDelete = async (user) => {
    if (window.confirm(`Are you sure you want to delete ${user.name}? This will remove all their progress data.`)) {
      try {
        await deleteUser(user.id);
      } catch (err) {
        alert(`Error deleting user: ${err.message}`);
      }
    }
  };

  /**
   * Handle toggling user active/suspended status
   */
  const handleToggleActive = async (user) => {
    const action = user.isActive ? 'suspend' : 'activate';
    if (window.confirm(`Are you sure you want to ${action} ${user.name}?`)) {
      try {
        await updateUser(user.id, { isActive: !user.isActive });
      } catch (err) {
        alert(`Error updating user status: ${err.message}`);
      }
    }
  };

  /**
   * Handle opening password reset modal
   */
  const handleResetPassword = (user) => {
    setPasswordResetUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError(null);
    setShowPasswordModal(true);
  };

  /**
   * Handle password reset submission
   */
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError(null);

    // Validation
    if (!newPassword) {
      setPasswordError('Password is required');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    try {
      const sendNotificationEmail = window.confirm(
        `Do you want to send a notification email to ${passwordResetUser.name} about this password change?`
      );
      await setPassword(passwordResetUser.id, newPassword, { sendNotificationEmail });
      setShowPasswordModal(false);
      alert(`Password successfully reset for ${passwordResetUser.name}`);
    } catch (err) {
      setPasswordError(err.message);
    }
  };

  /**
   * Get role badge variant
   */
  const getRoleBadgeVariant = (role) => {
    switch (role) {
      case 'admin':
        return 'danger';
      case 'course_manager':
        return 'warning';
      default:
        return 'primary';
    }
  };

  const formatLastActivity = (user) => {
    const activityCandidate = user?.lastActivityAt || user?.lastLoginAt || null;
    if (!activityCandidate) {
      return 'Never';
    }

    const parsedDate = new Date(activityCandidate);
    if (Number.isNaN(parsedDate.getTime())) {
      return 'Never';
    }

    return parsedDate.toLocaleDateString();
  };

  if (loading) {
    return <div className="text-center py-4">Loading users...</div>;
  }

  return (
    <>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">User Management</h5>
          <Button variant="primary" size="sm" onClick={handleCreate}>
            + Add User
          </Button>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          
          {users.length === 0 ? (
            <div className="text-center py-4 text-muted">
              No users found. Create your first user to get started.
            </div>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Organization</th>
                  <th>Status</th>
                  <th>Enrolled Courses</th>
                  <th>Managed Courses</th>
                  <th>Last Activity</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id}>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    <td>
                      <Badge bg={getRoleBadgeVariant(user.role)}>
                        {user.role}
                      </Badge>
                    </td>
                    <td>{user.organizationName || <span className="text-muted">—</span>}</td>
                    <td>
                      <Badge bg={user.isActive ? 'success' : 'secondary'}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td>{user.enrolledCourses.length}</td>
                    <td>
                      {user.role === 'course_manager' ? (
                        <span title={`${(user.managedCourses || []).length} assigned`}>
                          {(user.managedCourses || []).length}
                          {user.courseLicenseLimit > 0 && (
                            <span className="text-muted"> / {user.courseLicenseLimit}</span>
                          )}
                          {user.courseLicenseLimit > 0 && (user.managedCourses || []).length >= user.courseLicenseLimit && (
                            <Badge bg="warning" text="dark" className="ms-1" pill>At limit</Badge>
                          )}
                        </span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      {formatLastActivity(user)}
                    </td>
                    <td>
                      <ButtonGroup size="sm">
                        <Button 
                          variant="outline-primary" 
                          onClick={() => handleEdit(user)}
                        >
                          Edit
                        </Button>
                        <Button 
                          variant={user.isActive ? 'outline-warning' : 'outline-success'}
                          onClick={() => handleToggleActive(user)}
                          title={user.isActive ? 'Suspend account' : 'Activate account'}
                        >
                          {user.isActive ? 'Suspend' : 'Activate'}
                        </Button>
                        <Button 
                          variant="outline-warning" 
                          onClick={() => handleResetPassword(user)}
                          title="Reset Password"
                        >
                          🔑
                        </Button>
                        <Button 
                          variant="outline-info" 
                          onClick={() => onViewProgress(user)}
                        >
                          Progress
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          onClick={() => handleDelete(user)}
                        >
                          Delete
                        </Button>
                      </ButtonGroup>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* User Form Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {editingUser ? 'Edit User' : 'Create New User'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {formError && <Alert variant="danger">{formError}</Alert>}

            <div className="row g-3">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>First Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    placeholder="Enter first name"
                  />
                </Form.Group>
              </div>

              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Last Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    placeholder="Enter last name"
                  />
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Email *</Form.Label>
              <Form.Control
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="user@example.com"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              >
                <option value="student">Student</option>
                <option value="course_manager">Course Manager</option>
                <option value="admin">Admin</option>
              </Form.Select>
            </Form.Group>

            {formData.role === 'course_manager' && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Organization *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.organizationName}
                    onChange={(e) => setFormData({ ...formData, organizationName: e.target.value })}
                    placeholder="Enter organization/company name"
                  />
                  <Form.Text className="text-muted">
                    Course managers can only manage learners within this organization.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Course License Limit</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={formData.courseLicenseLimit}
                    onChange={(e) => setFormData({ ...formData, courseLicenseLimit: e.target.value })}
                    placeholder="0 = unlimited"
                  />
                  <Form.Text className="text-muted">
                    Maximum number of courses this manager can be assigned. Set to 0 for unlimited.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Assigned Courses</Form.Label>
                  <Form.Select
                    multiple
                    value={formData.managedCourses}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      setFormData({ ...formData, managedCourses: selected });
                    }}
                  >
                    {courses.map((course) => (
                      <option key={course.id} value={course.id}>
                        {course.title}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Hold Ctrl (Cmd on Mac) to select multiple courses.
                    {formData.courseLicenseLimit > 0 && (
                      <span className="ms-1 text-warning">
                        License allows up to {formData.courseLicenseLimit} course{Number(formData.courseLicenseLimit) !== 1 ? 's' : ''}.
                      </span>
                    )}
                  </Form.Text>
                </Form.Group>
              </>
            )}

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Active"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingUser ? 'Update User' : 'Create User'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Password Reset Modal */}
      <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)}>
        <Form onSubmit={handlePasswordSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              Reset Password for {passwordResetUser?.name}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {passwordError && <Alert variant="danger">{passwordError}</Alert>}
            
            <Alert variant="info">
              <strong>Note:</strong> Setting a new password will allow the user to log in with this password.
            </Alert>

            <Form.Group className="mb-3">
              <Form.Label>New Password *</Form.Label>
              <Form.Control
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
                autoComplete="new-password"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Confirm Password *</Form.Label>
              <Form.Control
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                autoComplete="new-password"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPasswordModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Reset Password
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
}

UserManager.propTypes = {
  onViewProgress: PropTypes.func,
};
